import { ExternalLink } from "lucide-react";
import type { InboxEngagement, InboxReply } from "../types/inbox";
import { ReplyVariants } from "./ReplyVariants";
import { useMarkReplied } from "../hooks/useMarkReplied";
import { useDiscard } from "../hooks/useDiscard";

interface Props {
  engagement: InboxEngagement;
  replies: InboxReply[];
}

// Guard against unsafe URLs from the scraping pipeline (e.g. javascript:).
const httpsUrl = (raw: string | undefined): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
};

export const EngagementCard = ({ engagement, replies }: Props) => {
  const markReplied = useMarkReplied();
  const discard = useDiscard();
  const reply = replies[0];
  const replyUrl = httpsUrl(engagement.urlToReply);

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <span className="text-sm mr-1">{engagement.type === "comment" ? "💬" : "📩"}</span>
          <span className="font-medium">{engagement.author.name}</span>
          {engagement.author.headline ? (
            <span className="text-slate-500"> · {engagement.author.headline}</span>
          ) : null}
        </div>
        {replyUrl ? (
          <a
            href={replyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
          >
            <ExternalLink size={12} /> Ver no LinkedIn
          </a>
        ) : null}
      </div>
      <blockquote className="text-sm text-slate-700 border-l-4 border-slate-200 pl-3 italic">
        {engagement.text}
      </blockquote>
      {reply ? (
        <ReplyVariants reply={reply} />
      ) : (
        <p className="text-xs text-slate-400">Aguardando sugestões…</p>
      )}
      <div className="flex gap-2 pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => markReplied.mutate(engagement.id)}
          disabled={markReplied.isPending}
          className="text-xs px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
        >
          Marcar respondido
        </button>
        <button
          type="button"
          onClick={() => discard.mutate(engagement.id)}
          disabled={discard.isPending}
          className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Descartar
        </button>
      </div>
    </div>
  );
};
